import React from 'react';

export interface IconButtonProps {
  children: React.ReactNode;
  variant?: 'teal' | 'purple' | 'green' | 'red' | 'gray';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  'aria-label'?: string;
}

/**
 * IconButton Component v2 - FacturXPro Design System
 *
 * Bouton icône avec variantes de couleurs et hover effects
 *
 * @example
 * ```tsx
 * <IconButton variant="teal" onClick={handleEdit}>
 *   <Edit className="w-4 h-4" />
 * </IconButton>
 *
 * <IconButton variant="red" size="sm" onClick={handleDelete}>
 *   <Trash className="w-4 h-4" />
 * </IconButton>
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Icon Button"
 */
export const IconButton: React.FC<IconButtonProps> = ({
  children,
  variant = 'gray',
  size = 'md',
  disabled = false,
  className = '',
  onClick,
  type = 'button',
  'aria-label': ariaLabel,
}) => {
  const variantClasses = {
    teal: 'bg-teal-50 text-teal-600 hover:bg-teal-100 hover:text-teal-700',
    purple:
      'bg-purple-50 text-purple-600 hover:bg-purple-100 hover:text-purple-700',
    green: 'bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700',
    red: 'bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700',
    gray: 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-700',
  };

  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  };

  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed hover:bg-current'
    : '';

  return (
    <button
      type={type}
      className={`
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${disabledClasses}
        rounded-lg
        transition-all
        duration-200
        flex
        items-center
        justify-center
        ${className}
      `.trim()}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {children}
    </button>
  );
};

export default IconButton;
